// ---------------------------------
// client/src/pages/PdfFormFill.jsx
// PDF form fill experience for users
// ---------------------------------
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SignaturePad from '../components/SignaturePad';
import PdfFieldTextBox from '../components/PdfFieldTextBox';
import { saveBlob } from '../utils/download';
import {
    fetchPdfForm,
    submitPdfForm,
    updatePdfForm,
    reopenPdfForm,
    storeCachedPdfFormExport,
    downloadCachedPdfFormExport,
} from '../utils/pdfForms';
import { fetchPdfTemplate, createTemplatePrefill } from '../utils/pdfTemplates';
import { exportPdfFormWithOverlays, buildFormFilename } from '../utils/pdfExport';
import { getPdfPageSize, loadPdfDocument, renderPdfPage } from '../utils/pdfjs';
import { formatDate } from '../utils/date';
import { resolveAssetUrl } from '../utils/assets';
import {
    getValueDisplay as getSharedValueDisplay,
    isFieldValueEmpty as isSharedFieldValueEmpty,
    isRangeField,
    normalizeCheckboxValue as normalizeSharedCheckboxValue,
    normalizeRangeValue,
    normalizeStoredFieldValue,
    normalizeValuesJson as normalizeSharedValuesJson,
    resolveAlign as resolveSharedAlign,
    resolveDirection as resolveSharedDirection,
    resolveFieldValue as resolveSharedFieldValue,
    resolveJustifyContent as resolveSharedJustifyContent,
} from '../utils/formFieldLayout';
import '../css/pdfFormFill.css';

const HIT_PADDING = 0;
const MIN_HIT_SIZE = 0;
const HISTORY_LIMIT = 50;
const RECENT_SUGGESTIONS_LIMIT = 5;
const RECENT_SUGGESTIONS_MAX_LENGTH = 160;
const RECENT_SUGGESTION_FIELD_TYPES = new Set(['text', 'number']);
const NON_INTERACTIVE_TYPES = new Set(['image', 'rectangle']);
const KEYBOARD_NAV_FIELD_TYPES = new Set(['text', 'number', 'date']);
const RECTANGLE_DEFAULT_STYLE = {
    strokeColor: '#ffffff',
    fillColor: '#ffffff',
    fillEnabled: true,
    strokeWidth: 1,
    opacity: 1,
};

function getExportFormName(template, formInstance) {
    const baseName = template?.name || 'form';
    if (!template?.isRepeatableInJobs) return baseName;
    const number = Number(formInstance?.instanceNumber);
    const instanceNumber = Number.isFinite(number) && number > 0 ? number : 1;
    return `${baseName} ${instanceNumber}`;
}

const TEXT = {
    back: 'חזור',
    loading: 'טוען טופס...',
    save: 'שמירת טיוטה',
    saving: 'שומר...',
    submit: 'שלח',
    submitting: 'שולח...',
    markDone: 'סמן כבוצע',
    markingDone: 'מסמן כבוצע...',
    export: 'יצוא PDF',
    exporting: 'מייצא...',
    reopen: 'עריכה מחדש',
    reopening: 'פותח לעריכה...',
    reopenConfirm: 'הטופס סומן כבוצע. לפתוח אותו לעריכה מחדש?',
    reopenError: 'לא ניתן לפתוח לעריכה כעת.',
    fit: 'התאם לרוחב',
    fullSize: '100%',
    zoom: 'זום',
    zoomIn: 'הגדל',
    zoomOut: 'הקטן',
    page: 'עמוד',
    pageOf: 'מתוך',
    next: 'הבא',
    previous: 'הקודם',
    emptyFields: 'אין שדות בשלב זה.',
    sections: 'שלבים',
    fields: 'שדות',
    done: 'סיום',
    nextSection: 'לשלב הבא',
    undo: 'בטל',
    redo: 'החזר',
    lastSaved: 'נשמר לאחרונה',
    autoSaveError: 'שגיאה בשמירה אוטומטית.',
    missingRequired: 'יש למלא את כל שדות החובה לפני סיום.',
    signatureTitle: 'חתימה',
    openSignature: 'הוסף חתימה',
    updateSignature: 'עדכן חתימה',
    clearSignature: 'נקה חתימה',
    close: 'סגור',
    errorLoad: 'לא ניתן לטעון את הטופס.',
    lockedTitle: 'טופס נעול לעריכה',
    lockedHint: 'הטופס נערך כעת על ידי',
    lockedUnknown: 'משתמש אחר',
    lockedViewOnly: 'לא ניתן לערוך את הטופס כרגע.',
    prefillLabel: 'מילוי מראש',
    prefillSelect: 'בחר מילוי מראש',
    prefillApply: 'החל מילוי',
    prefillSave: 'שמור מילוי מראש',
    prefillName: 'שם מילוי מראש',
    prefillSaveSuccess: 'מילוי מראש נשמר.',
    prefillSaveError: 'לא ניתן לשמור מילוי מראש.',
    suggestedValuesGroup: 'ערכים מוצעים',
    recentValuesGroup: 'ערכים אחרונים',
    autoFillLabel: 'AUTOFILL',
    clearAll: 'נקה את כל השדות',
    clearConfirm: 'למחוק את כל הערכים שמולאו? פעולה זו אינה הפיכה.',
    fillToday: 'מלא תאריך היום',
    fillNow: 'מלא שעה נוכחית',
    fillFrom: 'מ-',
    fillTo: 'עד',
    toolbarShow: 'הצג כלים',
    toolbarHide: 'הסתר כלים',
    editorShow: 'הצג מילוי',
    editorHide: 'הסתר מילוי',
};

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

function getInitials(value) {
    if (!value) return '—';
    const parts = value.trim().split(/\s+/);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : '';
    return `${first}${last}` || '—';
}

function CalendarQuickFillIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" strokeWidth="1.8" />
            <path d="M8 3.5v3M16 3.5v3M4 9h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            <path d="m9 14 2 2 4-4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function ClockQuickFillIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
            <path d="M12 7.5V12l3 2" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
            <path d="m7 18 2-2m8 0 2 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
    );
}

function normalizeOptions(value) {
    if (!value) return [];
    if (Array.isArray(value)) {
        return value.filter(Boolean);
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return [];
        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
                return parsed.filter(Boolean);
            }
        } catch {
            // fall through
        }
        return trimmed
            .split(/\r?\n|,/)
            .map((entry) => entry.trim())
            .filter(Boolean);
    }
    return [];
}

function normalizeSuggestions(value) {
    if (!value) return [];
    if (Array.isArray(value)) {
        return value.map((entry) => String(entry || '').trim()).filter(Boolean);
    }
    if (typeof value === 'string') {
        return value
            .split(/\r?\n/)
            .map((entry) => entry.trim())
            .filter(Boolean);
    }
    return [];
}

function normalizeSuggestionKey(value) {
    return String(value || '').trim().toLowerCase();
}

function isRecentSuggestionField(field) {
    if (!field || !RECENT_SUGGESTION_FIELD_TYPES.has(field.type)) return false;
    if (field.autoFill) return false;
    if (field.type === 'text' && field.isReadOnly) return false;
    return true;
}

function isSuggestionDisplayField(field) {
    if (!field || !RECENT_SUGGESTION_FIELD_TYPES.has(field.type)) return false;
    if (field.type === 'text' && field.isReadOnly) return false;
    return true;
}

function isRecentSuggestionValueAllowed(field, value) {
    const text = String(value ?? '').trim();
    if (!text) return false;
    if (text.length > RECENT_SUGGESTIONS_MAX_LENGTH) return false;
    if (/^data:/i.test(text)) return false;
    if (field?.type === 'number' && !/^-?\d+(\.\d+)?$/.test(text)) return false;
    return true;
}

function getRecentSuggestionStorageKey({ userId, templateId, fieldId }) {
    if (!templateId || !fieldId) return '';
    return `pdfRecentValues:v1:${userId || 'local'}:${templateId}:${fieldId}`;
}

function readRecentFieldSuggestions({ userId, templateId, field }) {
    if (typeof window === 'undefined' || !isRecentSuggestionField(field)) return [];
    const key = getRecentSuggestionStorageKey({ userId, templateId, fieldId: field.id });
    if (!key) return [];
    try {
        const parsed = JSON.parse(window.localStorage.getItem(key) || '[]');
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((entry) => {
                if (typeof entry === 'string') {
                    return { value: entry, count: 1, lastUsedAt: '' };
                }
                return {
                    value: String(entry?.value || '').trim(),
                    count: Number(entry?.count) || 1,
                    lastUsedAt: entry?.lastUsedAt || '',
                };
            })
            .filter((entry) => isRecentSuggestionValueAllowed(field, entry.value))
            .sort((a, b) => {
                const timeDiff = new Date(b.lastUsedAt || 0).getTime() - new Date(a.lastUsedAt || 0).getTime();
                if (timeDiff !== 0) return timeDiff;
                return (Number(b.count) || 0) - (Number(a.count) || 0);
            })
            .slice(0, RECENT_SUGGESTIONS_LIMIT)
            .map((entry) => entry.value);
    } catch {
        return [];
    }
}

function writeRecentFieldSuggestion({ userId, templateId, field, value }) {
    if (typeof window === 'undefined' || !isRecentSuggestionField(field)) return;
    const nextValue = String(value ?? '').trim();
    if (!isRecentSuggestionValueAllowed(field, nextValue)) return;
    const key = getRecentSuggestionStorageKey({ userId, templateId, fieldId: field.id });
    if (!key) return;
    try {
        const existing = JSON.parse(window.localStorage.getItem(key) || '[]');
        const entries = Array.isArray(existing) ? existing : [];
        const now = new Date().toISOString();
        const normalizedNext = normalizeSuggestionKey(nextValue);
        const merged = [
            {
                value: nextValue,
                count: 1,
                lastUsedAt: now,
            },
        ];
        entries.forEach((entry) => {
            const valueText = String((typeof entry === 'string' ? entry : entry?.value) || '').trim();
            if (!valueText || normalizeSuggestionKey(valueText) === normalizedNext) {
                if (normalizeSuggestionKey(valueText) === normalizedNext) {
                    merged[0].count = (Number(entry?.count) || 1) + 1;
                }
                return;
            }
            if (!isRecentSuggestionValueAllowed(field, valueText)) return;
            merged.push({
                value: valueText,
                count: Number(entry?.count) || 1,
                lastUsedAt: entry?.lastUsedAt || '',
            });
        });
        window.localStorage.setItem(key, JSON.stringify(merged.slice(0, RECENT_SUGGESTIONS_LIMIT)));
    } catch {
        // Local suggestions are a convenience only.
    }
}

function normalizeNumericConfig(value) {
    if (!value) return {};
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch {
            return {};
        }
    }
    if (typeof value === 'object') {
        return value;
    }
    return {};
}

function normalizeRectStyle(value) {
    if (!value) return { ...RECTANGLE_DEFAULT_STYLE };
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            if (parsed && typeof parsed === 'object') {
                return { ...RECTANGLE_DEFAULT_STYLE, ...parsed };
            }
        } catch {
            return { ...RECTANGLE_DEFAULT_STYLE };
        }
    }
    if (typeof value === 'object') {
        return { ...RECTANGLE_DEFAULT_STYLE, ...value };
    }
    return { ...RECTANGLE_DEFAULT_STYLE };
}

function hasValidRect(field) {
    const xNorm = Number(field?.xNorm);
    const yNorm = Number(field?.yNorm);
    const wNorm = Number(field?.wNorm);
    const hNorm = Number(field?.hNorm);
    if (!Number.isFinite(xNorm) || !Number.isFinite(yNorm) || !Number.isFinite(wNorm) || !Number.isFinite(hNorm)) {
        return false;
    }
    return wNorm > 0 && hNorm > 0;
}

function isInteractiveField(field) {
    if (!field) return false;
    if (!hasValidRect(field)) return false;
    if (NON_INTERACTIVE_TYPES.has(field.type)) return false;
    if (field.type === 'text' && field.isReadOnly) return false;
    return true;
}

function isKeyboardNavigationField(field) {
    if (!field) return false;
    if (!KEYBOARD_NAV_FIELD_TYPES.has(field.type)) return false;
    if (field.type === 'text' && field.isReadOnly) return false;
    return true;
}

function normalizeValuesJson(value) {
    return normalizeSharedValuesJson(value);
}

function normalizeCheckboxValue(value) {
    return normalizeSharedCheckboxValue(value);
}

function getCheckboxDisplayValue(value) {
    const normalized = normalizeCheckboxValue(value);
    if (normalized === 'checked') return '✓';
    if (normalized === 'cross') return 'X';
    return '';
}

function getNextCheckboxValue(value) {
    const normalized = normalizeCheckboxValue(value);
    if (normalized === 'checked') return 'cross';
    if (normalized === 'cross') return '';
    return 'checked';
}

function containsHebrew(value) {
    return /[\u0590-\u05ff]/.test(value || '');
}

function normalizeAlignValue(value) {
    const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
    if (normalized === 'left' || normalized === 'center' || normalized === 'right') {
        return normalized;
    }
    return '';
}

function normalizeDirectionValue(value) {
    const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
    if (normalized === 'rtl' || normalized === 'ltr') {
        return normalized;
    }
    return '';
}

function resolveDirection(field, value) {
    return resolveSharedDirection(field, value);
}

function resolveAlign(field, direction) {
    return resolveSharedAlign(field, direction);
}

function resolveJustifyContent(align, direction) {
    return resolveSharedJustifyContent(align, direction);
}

function getValueDisplay(field, value) {
    return getSharedValueDisplay(field, value, { formatDateFn: formatDate });
}

function formatDateValue(date) {
    return formatDate(date);
}

function formatTimeValue(date) {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
}

function resolveAutoFillValue(autoFill, context) {
    if (!autoFill) return '';
    const now = new Date();
    switch (autoFill) {
        case 'job_number':
            return context.job?.jobNumber || '';
        case 'worker_name':
            return context.user?.name || '';
        case 'worker_role':
            return context.user?.occupation || '';
        case 'recipient_company':
            return context.job?.recipientCompanyName || '';
        case 'date_today':
            return formatDateValue(now);
        case 'date_form_start':
            return context.formStartDate ? formatDateValue(context.formStartDate) : formatDateValue(now);
        case 'date_on_click':
            return formatDateValue(now);
        case 'time_now':
            return formatTimeValue(now);
        case 'time_form_start':
            return context.formStartDate ? formatTimeValue(context.formStartDate) : formatTimeValue(now);
        case 'time_on_click':
            return formatTimeValue(now);
        default:
            return '';
    }
}

function resolveFieldValue(field, values) {
    return resolveSharedFieldValue(field, values);
}

function normalizeDateValue(value) {
    if (value === null || value === undefined || value === '') return value;
    const formatted = formatDate(value);
    return formatted || value;
}

function normalizeDateFieldValue(field, value) {
    if (isRangeField(field)) {
        const normalized = normalizeRangeValue(value);
        return {
            from: normalizeDateValue(normalized.from) || '',
            to: normalizeDateValue(normalized.to) || '',
        };
    }
    return normalizeDateValue(value);
}

function getEmptyFieldValue(field) {
    if (field?.type === 'checkbox') {
        return '';
    }
    if (isRangeField(field)) {
        return { from: '', to: '' };
    }
    return '';
}

function setRangeSideValue(field, value, side, nextSideValue) {
    if (!isRangeField(field)) {
        return nextSideValue;
    }
    const normalized = normalizeRangeValue(value);
    return {
        ...normalized,
        [side]: nextSideValue,
    };
}

function isFieldValueEmpty(field, value) {
    return isSharedFieldValueEmpty(field, value);
}

function toRenderRect(field, pageSize) {
    if (!hasValidRect(field)) return null;
    const xNorm = Number(field.xNorm);
    const yNorm = Number(field.yNorm);
    const wNorm = Number(field.wNorm);
    const hNorm = Number(field.hNorm);
    if (!Number.isFinite(xNorm) || !Number.isFinite(yNorm) || !Number.isFinite(wNorm) || !Number.isFinite(hNorm)) {
        return null;
    }
    return {
        x: xNorm * pageSize.width,
        y: yNorm * pageSize.height,
        width: wNorm * pageSize.width,
        height: hNorm * pageSize.height,
    };
}

function toHitRect(renderRect, pageSize) {
    const paddedWidth = Math.max(renderRect.width + HIT_PADDING * 2, MIN_HIT_SIZE);
    const paddedHeight = Math.max(renderRect.height + HIT_PADDING * 2, MIN_HIT_SIZE);
    const centerX = renderRect.x + renderRect.width / 2;
    const centerY = renderRect.y + renderRect.height / 2;
    const x = clamp(centerX - paddedWidth / 2, 0, Math.max(pageSize.width - paddedWidth, 0));
    const y = clamp(centerY - paddedHeight / 2, 0, Math.max(pageSize.height - paddedHeight, 0));
    return { x, y, width: paddedWidth, height: paddedHeight };
}

function shortenFieldLabel(value, maxLength = 36) {
    const label = String(value || '').trim();
    if (!label) {
        return '';
    }
    if (label.length <= maxLength) {
        return label;
    }
    return `${label.slice(0, Math.max(0, maxLength - 3)).trim()}...`;
}

function buildMissingRequiredGroups(fields = []) {
    const groups = new Map();
    fields
        .slice()
        .sort((a, b) => {
            const pageDiff = (Number(a?.pageIndex) || 0) - (Number(b?.pageIndex) || 0);
            if (pageDiff !== 0) return pageDiff;
            const yDiff = (Number(a?.yNorm) || 0) - (Number(b?.yNorm) || 0);
            if (Math.abs(yDiff) > 0.0005) return yDiff;
            return (Number(a?.xNorm) || 0) - (Number(b?.xNorm) || 0);
        })
        .forEach((field) => {
        const pageIndex = Number.isFinite(Number(field?.pageIndex)) ? Number(field.pageIndex) : 0;
        if (!groups.has(pageIndex)) {
            groups.set(pageIndex, []);
        }
        const displayLabel =
            shortenFieldLabel(field?.label) ||
            shortenFieldLabel(field?.key) ||
            shortenFieldLabel(field?.type) ||
            'שדה';
        groups.get(pageIndex).push({
            id: field.id,
            label: displayLabel,
            fullLabel: field?.label || field?.key || field?.type || '',
        });
        });
    return [...groups.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([pageIndex, pageFields]) => ({
            pageIndex,
            count: pageFields.length,
            fields: pageFields,
        }));
}

export default function PdfFormFill({ previewTemplateId = null, previewMode = false }) {
    const { id } = useParams();
    const isPreview = Boolean(previewTemplateId) || previewMode;
    const navigate = useNavigate();
    const toolbarRef = useRef(null);
    const viewportRef = useRef(null);
    const canvasRef = useRef(null);
    const pointerState = useRef({ pointers: new Map(), lastCenter: null, lastDistance: 0 });
    const panState = useRef({ isPanning: false, startX: 0, startY: 0, origin: { x: 0, y: 0 } });
    const editorInputRef = useRef(null);
    const preserveEditorFocusRef = useRef(false);
    const editorTouchStartYRef = useRef(null);
    const editorTouchHandledRef = useRef(false);

    const [formInstance, setFormInstance] = useState(null);
    const [template, setTemplate] = useState(null);
    const [values, setValues] = useState({});
    const [pdfDoc, setPdfDoc] = useState(null);
    const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
    const [fitScale, setFitScale] = useState(1);
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [currentPage, setCurrentPage] = useState(0);
    const [activeSectionIndex, setActiveSectionIndex] = useState(0);
    const [activeFieldId, setActiveFieldId] = useState(null);
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [isCompactLayout, setIsCompactLayout] = useState(false);
    const [isMobileToolbarExpanded, setIsMobileToolbarExpanded] = useState(false);
    const [isSignatureOpen, setIsSignatureOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [isReopening, setIsReopening] = useState(false);
    const [prefillOptions, setPrefillOptions] = useState([]);
    const [selectedPrefillId, setSelectedPrefillId] = useState('');
    const [isPrefillModalOpen, setIsPrefillModalOpen] = useState(false);
    const [prefillName, setPrefillName] = useState('');
    const [prefillFeedback, setPrefillFeedback] = useState('');
    const [prefillError, setPrefillError] = useState('');
    const [isSavingPrefill, setIsSavingPrefill] = useState(false);
    const [error, setError] = useState('');
    const [missingRequiredGroups, setMissingRequiredGroups] = useState([]);
    const [isLocked, setIsLocked] = useState(false);
    const [toolbarHeight, setToolbarHeight] = useState(0);
    const [siteHeaderHeight, setSiteHeaderHeight] = useState(96);

    useEffect(() => {
        if (typeof document === 'undefined') {
            return undefined;
        }
        document.body.classList.add('pdf-form-fill-active');
        return () => {
            document.body.classList.remove('pdf-form-fill-active');
        };
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) {
            return undefined;
        }
        const media = window.matchMedia('(max-width: 768px)');
        const update = () => setIsCompactLayout(media.matches);
        update();
        if (media.addEventListener) {
            media.addEventListener('change', update);
        } else {
            media.addListener(update);
        }
        return () => {
            if (media.removeEventListener) {
                media.removeEventListener('change', update);
            } else {
                media.removeListener(update);
            }
        };
    }, [isPreview]);

    useEffect(() => {
        const toolbar = toolbarRef.current;
        const siteHeader = document.querySelector('.header');
        if (!toolbar) {
            return undefined;
        }

        const updateToolbarHeight = () => {
            setToolbarHeight(Math.ceil(toolbar.getBoundingClientRect().height || 0));
        };
        const updateHeaderHeight = () => {
            setSiteHeaderHeight(Math.ceil(siteHeader?.getBoundingClientRect().height || 96));
        };

        updateToolbarHeight();
        updateHeaderHeight();

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', updateToolbarHeight);
            window.addEventListener('resize', updateHeaderHeight);
            return () => {
                window.removeEventListener('resize', updateToolbarHeight);
                window.removeEventListener('resize', updateHeaderHeight);
            };
        }

        const observer = new ResizeObserver(() => {
            updateToolbarHeight();
            updateHeaderHeight();
        });
        observer.observe(toolbar);
        if (siteHeader) {
            observer.observe(siteHeader);
        }
        window.addEventListener('resize', updateToolbarHeight);
        window.addEventListener('resize', updateHeaderHeight);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateToolbarHeight);
            window.removeEventListener('resize', updateHeaderHeight);
        };
    }, []);

    const [lockedBy, setLockedBy] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [valuesHistory, setValuesHistory] = useState({ past: [], future: [] });

    const initialLoadRef = useRef(true);
    const autoSaveTimerRef = useRef(null);

    const hydrateFormInstance = useCallback(async (data) => {
        setFormInstance(data);
        setIsLocked(Boolean(data.locked));
        setLockedBy(data.lockedBy || null);
        setCurrentUser(data.currentUser || data.user || null);
        const normalizedTemplate = data.template
            ? {
                  ...data.template,
                  fields: (data.template.fields || []).map((field) => ({
                      ...field,
                      optionsJson: normalizeOptions(field.optionsJson),
                      numericConfigJson: normalizeNumericConfig(field.numericConfigJson),
                      styleJson:
                          field.type === 'rectangle'
                              ? normalizeRectStyle(field.styleJson)
                              : field.styleJson || null,
                      assetDataUrl: typeof field.assetDataUrl === 'string' ? field.assetDataUrl : '',
                      isMultiline: Boolean(field.isMultiline),
                      isReadOnly: Boolean(field.isReadOnly),
                      staticValue: field.staticValue || '',
                      subtext: field.subtext || '',
                      fontSizeMode: field.fontSizeMode || 'auto_fit',
                      wrapMode: field.wrapMode || 'same_word',
                      rangeMode: field.rangeMode || 'single',
                      suggestedValues: typeof field.suggestedValues === 'string' ? field.suggestedValues : '',
                  })),
              }
            : null;
        setTemplate(normalizedTemplate);
        const incomingPrefills = Array.isArray(data.prefillOptions)
            ? data.prefillOptions
            : Array.isArray(normalizedTemplate?.prefillOptions)
                ? normalizedTemplate.prefillOptions
                : [];
        setPrefillOptions(incomingPrefills);
        setSelectedPrefillId(incomingPrefills.length === 1 ? String(incomingPrefills[0].id) : '');
        const baseValues = normalizeValuesJson(data.valuesJson);
        const normalizedValues = { ...baseValues };
        const autoFillUpdates = {};
        const contextUser = data.currentUser || data.user || null;
        const context = {
            job: data.job || null,
            user: contextUser,
            formStartDate: data.createdAt ? new Date(data.createdAt) : null,
        };
        (normalizedTemplate?.fields || [])
            .filter((field) => field.type !== 'sectionBreak')
            .forEach((field) => {
                normalizedValues[field.id] = normalizeStoredFieldValue(field, normalizedValues[field.id]);
                if (field.autoFill) {
                    const currentValue = normalizedValues[field.id];
                    if (isPreview) {
                        if (isFieldValueEmpty(field, currentValue)) {
                            autoFillUpdates[field.id] = isRangeField(field)
                                ? { from: TEXT.autoFillLabel, to: '' }
                                : TEXT.autoFillLabel;
                        }
                        return;
                    }
                    if (field.autoFill === 'time_on_click' || field.autoFill === 'date_on_click') {
                        return;
                    }
                    if (!isFieldValueEmpty(field, currentValue)) {
                        return;
                    }
                    const filledValue = resolveAutoFillValue(field.autoFill, context);
                    if (filledValue !== '') {
                        autoFillUpdates[field.id] = isRangeField(field)
                            ? { ...normalizeRangeValue(currentValue), from: filledValue }
                            : filledValue;
                    }
                    return;
                }
            });
        (normalizedTemplate?.fields || [])
            .filter((field) => field.type === 'text' && field.isReadOnly && field.staticValue)
            .forEach((field) => {
                const currentValue = normalizedValues[field.id];
                if (!isFieldValueEmpty(field, currentValue)) {
                    return;
                }
                autoFillUpdates[field.id] = field.staticValue;
            });
        const nextValues = { ...normalizedValues, ...autoFillUpdates };
        (normalizedTemplate?.fields || [])
            .filter((field) => field.type === 'date')
            .forEach((field) => {
                const currentValue = nextValues[field.id];
                if (isFieldValueEmpty(field, currentValue)) return;
                nextValues[field.id] = normalizeDateFieldValue(field, currentValue);
            });
        setValues(nextValues);
        setValuesHistory({ past: [], future: [] });
        initialLoadRef.current = Object.keys(autoFillUpdates).length === 0;
        setLastSavedAt(data.updatedAt || null);
        const pdf = await loadPdfDocument(normalizedTemplate?.fileUrl || data.template?.fileUrl);
        setPdfDoc(pdf);
    }, []);

    const displayFields = useMemo(
        () =>
            (template?.fields || [])
                .map((field) => ({
                    ...field,
                    optionsJson: normalizeOptions(field.optionsJson),
                    numericConfigJson: normalizeNumericConfig(field.numericConfigJson),
                    styleJson: field.type === 'rectangle' ? normalizeRectStyle(field.styleJson) : field.styleJson || null,
                    assetDataUrl: typeof field.assetDataUrl === 'string' ? field.assetDataUrl : '',
                    isMultiline: Boolean(field.isMultiline),
                    isReadOnly: Boolean(field.isReadOnly),
                    staticValue: field.staticValue || '',
                    subtext: field.subtext || '',
                    fontSizeMode: field.fontSizeMode || 'auto_fit',
                    wrapMode: field.wrapMode || 'same_word',
                    rangeMode: field.rangeMode || 'single',
                    suggestedValues: typeof field.suggestedValues === 'string' ? field.suggestedValues : '',
                }))
                .filter((field) => field.type !== 'sectionBreak')
                .slice()
                .sort((a, b) => (Number(a.orderIndex) || 0) - (Number(b.orderIndex) || 0)),
        [template],
    );
    const interactiveFields = useMemo(
        () =>
            displayFields
                .filter((field) => isInteractiveField(field))
                .slice()
                .sort((a, b) => {
                    const seqA = Number(a.sequenceNumber) || Number(a.orderIndex) || 0;
                    const seqB = Number(b.sequenceNumber) || Number(b.orderIndex) || 0;
                    if (seqA !== seqB) return seqA - seqB;
                    return (Number(a.orderIndex) || 0) - (Number(b.orderIndex) || 0);
                }),
        [displayFields],
    );
    const sequencedFields = useMemo(
        () => interactiveFields.filter((field) => !field.skipSequence),
        [interactiveFields],
    );
    const pageCount = pdfDoc?.numPages || 1;

    const sections = useMemo(() => {
        const baseSections = (template?.sections || []).length
            ? [...template.sections].sort((a, b) => a.orderIndex - b.orderIndex)
            : [{ id: 'default', title: 'שלב 1', orderIndex: 0 }];
        const sectionMap = new Map(baseSections.map((section) => [section.id, { ...section, fields: [] }]));
        const fallbackSection = baseSections[0];
        sequencedFields.forEach((field) => {
            const sectionId = field.sectionId || fallbackSection?.id;
            const target = sectionMap.get(sectionId) || sectionMap.get(fallbackSection?.id);
            if (target) {
                target.fields.push(field);
            }
        });
        return baseSections.map((section) => {
            const entry = sectionMap.get(section.id);
            const sortedFields = (entry?.fields || []).slice().sort((a, b) => {
                const seqA = Number(a.sequenceNumber) || Number(a.orderIndex) || 0;
                const seqB = Number(b.sequenceNumber) || Number(b.orderIndex) || 0;
                if (seqA !== seqB) return seqA - seqB;
                return (Number(a.orderIndex) || 0) - (Number(b.orderIndex) || 0);
            });
            return { ...section, fields: sortedFields };
        });
    }, [template, sequencedFields]);

    const activeSection = sections[activeSectionIndex] || sections[0];
    const activeFields = activeSection?.fields || [];
    const activeField = interactiveFields.find((field) => field.id === activeFieldId) || activeFields[0];
    const isPreviewAutoFill = isPreview && Boolean(activeField?.autoFill);
    const activeFieldValue = activeField ? resolveFieldValue(activeField, values) : '';
    const activeRangeValue = activeField ? normalizeRangeValue(activeFieldValue) : { from: '', to: '' };
    const activeFieldDisplayValue = activeField ? getValueDisplay(activeField, activeFieldValue) : '';
    const isJobCompleted = ['completed', 'archived'].includes(formInstance?.job?.status || '');
    const isReadOnly = formInstance?.status === 'submitted' || isLocked || isJobCompleted;
    const activeSuggestions = useMemo(
        () => normalizeSuggestions(activeField?.suggestedValues),
        [activeField],
    );
    const recentSuggestions = useMemo(
        () =>
            readRecentFieldSuggestions({
                userId: currentUser?.id || formInstance?.userId || '',
                templateId: template?.id,
                field: activeField,
            }),
        [activeField, currentUser?.id, formInstance?.userId, template?.id],
    );
    const activeSuggestionGroups = useMemo(() => {
        if (!isSuggestionDisplayField(activeField) || isPreviewAutoFill || isReadOnly) {
            return [];
        }
        const input = String(activeFieldValue || '');
        const lowerInput = input.toLowerCase();
        const filterOptions = (options, existingKeys = new Set(), { allowExact = false } = {}) => options.filter((option) => {
            if (!option) return false;
            if (!allowExact && option === input) return false;
            const key = normalizeSuggestionKey(option);
            if (existingKeys.has(key)) return false;
            if (activeField.type === 'number' && !isRecentSuggestionValueAllowed(activeField, option)) return false;
            return lowerInput ? key.includes(lowerInput) : true;
        });
        const adminMatches = filterOptions(activeSuggestions);
        const adminKeys = new Set(adminMatches.map((option) => normalizeSuggestionKey(option)));
        const recentMatches = filterOptions(recentSuggestions, adminKeys, { allowExact: true });
        return [
            adminMatches.length > 0
                ? { id: 'admin', label: TEXT.suggestedValuesGroup, options: adminMatches }
                : null,
            recentMatches.length > 0
                ? { id: 'recent', label: TEXT.recentValuesGroup, options: recentMatches }
                : null,
        ].filter(Boolean);
    }, [activeField, activeFieldValue, activeSuggestions, isReadOnly, isPreviewAutoFill, recentSuggestions]);
    const activeSuggestionMatches = useMemo(
        () => activeSuggestionGroups.flatMap((group) => group.options),
        [activeSuggestionGroups],
    );
    const activeDirection = activeField ? resolveDirection(activeField, activeFieldDisplayValue) : 'rtl';
    const activeAlign = activeField ? resolveAlign(activeField, activeDirection) : 'right';
    const activeFieldIndex = activeFields.findIndex((field) => field.id === activeField?.id);
    const previousField = activeFieldIndex > 0 ? activeFields[activeFieldIndex - 1] : null;
    const nextField = activeFieldIndex >= 0 && activeFieldIndex < activeFields.length - 1
        ? activeFields[activeFieldIndex + 1]
        : null;
    const isLastFieldInSection = activeFieldIndex >= 0 && activeFieldIndex === activeFields.length - 1;
    const hasNextSection = activeSectionIndex < sections.length - 1;
    const nextSectionFirstField = hasNextSection
        ? sections[activeSectionIndex + 1]?.fields?.[0] || null
        : null;
    const orderedNavigableFields = sequencedFields.length > 0 ? sequencedFields : interactiveFields;
    const activeNavigableIndex = orderedNavigableFields.findIndex(
        (field) => field.id === activeField?.id,
    );
    const previousNavigableField = activeNavigableIndex > 0
        ? orderedNavigableFields[activeNavigableIndex - 1]
        : previousField;
    const nextNavigableField =
        activeNavigableIndex >= 0 && activeNavigableIndex < orderedNavigableFields.length - 1
            ? orderedNavigableFields[activeNavigableIndex + 1]
            : nextField;
    const isLastAccessibleField =
        activeNavigableIndex >= 0 &&
        orderedNavigableFields.length > 0 &&
        activeNavigableIndex === orderedNavigableFields.length - 1;
    const interactiveFieldIds = useMemo(
        () => new Set(interactiveFields.map((field) => field.id)),
        [interactiveFields],
    );
    const currentUserLabel = currentUser?.name || currentUser?.email || '';
    const currentUserAvatarUrl = resolveAssetUrl(currentUser?.profileImageUrl);
    const currentUserInitials = getInitials(currentUserLabel);

    useEffect(() => {
        const load = async () => {
            try {
                setError('');
                if (isPreview && previewTemplateId) {
                    const templateData = await fetchPdfTemplate(previewTemplateId);
                    const previewInstance = {
                        id: `preview-${templateData.id}`,
                        template: templateData,
                        valuesJson: {},
                        status: 'draft',
                        createdAt: new Date().toISOString(),
                        job: null,
                        user: null,
                        currentUser: null,
                        locked: false,
                        prefillOptions: Array.isArray(templateData.prefillOptions)
                            ? templateData.prefillOptions
                            : [],
                    };
                    await hydrateFormInstance(previewInstance);
                    return;
                }
                const data = await fetchPdfForm(id);
                await hydrateFormInstance(data);
            } catch (err) {
                setError(err.response?.data?.message || TEXT.errorLoad);
            }
        };
        load();
    }, [id, hydrateFormInstance, isPreview, previewTemplateId]);

    useEffect(() => {
        if (formInstance?.updatedAt) {
            setLastSavedAt(formInstance.updatedAt);
        }
    }, [formInstance?.updatedAt]);

    useEffect(() => {
        if (!pdfDoc || !viewportRef.current) return;
        const observer = new ResizeObserver(async (entries) => {
            const width = entries[0]?.contentRect?.width || 0;
            if (!width) return;
            const size = await getPdfPageSize(pdfDoc, currentPage);
            setFitScale(width / size.width);
        });
        observer.observe(viewportRef.current);
        return () => observer.disconnect();
    }, [pdfDoc, currentPage]);

    useEffect(() => {
        if (!pdfDoc || !fitScale || !canvasRef.current) return;
        let isMounted = true;
        const render = async () => {
            const { canvas, viewport } = await renderPdfPage(pdfDoc, currentPage, fitScale);
            if (!isMounted || !canvasRef.current) return;
            canvasRef.current.width = canvas.width;
            canvasRef.current.height = canvas.height;
            const context = canvasRef.current.getContext('2d');
            context.clearRect(0, 0, canvas.width, canvas.height);
            context.drawImage(canvas, 0, 0);
            setPageSize({ width: viewport.width, height: viewport.height });
            const viewportBox = viewportRef.current?.getBoundingClientRect();
            if (viewportBox) {
                const offsetX = viewportBox.width - viewport.width;
                const offsetY = viewportBox.height - viewport.height;
                const nextPan = {
                    x: offsetX < 0 ? 0 : offsetX / 2,
                    y: offsetY < 0 ? 0 : offsetY / 2,
                };
                setZoom(1);
                setPan(nextPan);
            }
        };
        render();
        return () => {
            isMounted = false;
        };
    }, [pdfDoc, currentPage, fitScale]);

    useEffect(() => {
        if (!activeField) return;
        setActiveFieldId(activeField.id);
    }, [activeField?.id]);

    const canReopenForm = formInstance?.status === 'submitted' && !isJobCompleted && !isLocked;
    const lastSavedLabel = lastSavedAt ? formatDate(lastSavedAt) : '';
    const showMissingRequiredSummary =
        missingRequiredGroups.length > 0 && typeof error === 'string' && error.startsWith(TEXT.missingRequired);
    const canUndo = valuesHistory.past.length > 0;
    const canRedo = valuesHistory.future.length > 0;
    const mergeFormInstance = useCallback((nextInstance) => {
        if (nextInstance?.locked !== undefined) {
            setIsLocked(Boolean(nextInstance.locked));
            setLockedBy(nextInstance.lockedBy || null);
        }
        if (nextInstance?.currentUser) {
            setCurrentUser(nextInstance.currentUser);
        }
        setFormInstance((prev) => {
            if (!prev) return nextInstance;
            return {
                ...prev,
                ...nextInstance,
                template: prev.template || nextInstance?.template,
                job: prev.job || nextInstance?.job,
                user: prev.user || nextInstance?.user,
            };
        });
    }, []);

    const refreshLockState = useCallback(async () => {
        if (isPreview) return;
        try {
            const latest = await fetchPdfForm(id);
            mergeFormInstance(latest);
        } catch {
            // ignore lock refresh errors
        }
    }, [id, isPreview, mergeFormInstance]);

    const formInstanceId = formInstance?.id;

    useEffect(() => {
        if (isPreview || !formInstanceId || isReadOnly) return;
        if (initialLoadRef.current) {
            initialLoadRef.current = false;
            return;
        }
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
        }
        autoSaveTimerRef.current = setTimeout(async () => {
            try {
                setIsSaving(true);
                const payload = values && typeof values === 'object' ? values : {};
                const updated = await updatePdfForm(id, payload);
                if (updated?.locked !== undefined) {
                    setIsLocked(Boolean(updated.locked));
                    setLockedBy(updated.lockedBy || null);
                }
                if (updated?.currentUser) {
                    setCurrentUser(updated.currentUser);
                }
                setLastSavedAt(updated?.updatedAt || new Date().toISOString());
            } catch (err) {
                if (err.response?.status === 423) {
                    setError(TEXT.lockedTitle);
                    await refreshLockState();
                    return;
                }
                setError(err.response?.data?.message || TEXT.autoSaveError);
            } finally {
                setIsSaving(false);
            }
        }, 900);
        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
        };
    }, [formInstanceId, id, isPreview, isReadOnly, mergeFormInstance, refreshLockState, values]);

    const handleValueChange = useCallback((fieldId, nextValue) => {
        if (isReadOnly) return;
        setError('');
        setMissingRequiredGroups([]);
        setValues((prev) => {
            const safePrev = prev && typeof prev === 'object' ? prev : {};
            const next = { ...safePrev, [fieldId]: nextValue };
            setValuesHistory((history) => {
                const past = [...history.past, safePrev].slice(-HISTORY_LIMIT);
                return { past, future: [] };
            });
            return next;
        });
    }, [isReadOnly]);

    const handleSuggestionPick = useCallback(
        (suggestion) => {
            if (!activeField || !suggestion) return;
            handleValueChange(activeField.id, suggestion);
        },
        [activeField, handleValueChange],
    );

    const handleSuggestionKeyDown = useCallback(
        (event) => {
            if (event.key !== 'Enter') return;
            if (activeSuggestionMatches.length === 0) return;
            if (!activeField || !RECENT_SUGGESTION_FIELD_TYPES.has(activeField.type)) return;
            if (activeField.isMultiline && event.shiftKey) return;
            event.preventDefault();
            handleSuggestionPick(activeSuggestionMatches[0]);
        },
        [activeField, activeSuggestionMatches, handleSuggestionPick],
    );

    const handleQuickDateTimeFill = useCallback(
        (field, side = 'from') => {
            if (!field || isReadOnly || !['date', 'time'].includes(field.type)) return;
            const now = new Date();
            const nextSideValue = field.type === 'date' ? formatDateValue(now) : formatTimeValue(now);
            const currentValue = resolveFieldValue(field, values);
            const nextValue = isRangeField(field)
                ? setRangeSideValue(field, currentValue, side, nextSideValue)
                : nextSideValue;
            handleValueChange(field.id, nextValue);
        },
        [handleValueChange, isReadOnly, values],
    );

    const renderDateTimeQuickFill = useCallback(
        (field) => {
            if (!field || !['date', 'time'].includes(field.type) || isPreviewAutoFill) {
                return null;
            }
            const label = field.type === 'date' ? TEXT.fillToday : TEXT.fillNow;
            const Icon = field.type === 'date' ? CalendarQuickFillIcon : ClockQuickFillIcon;
            if (isRangeField(field)) {
                return (
                    <div className="pdf-form-editor__quick-fill">
                        <button
                            type="button"
                            className="button button--light pdf-form-editor__quick-button"
                            onClick={() => handleQuickDateTimeFill(field, 'from')}
                            disabled={isReadOnly}
                        >
                            <span className="button__icon" aria-hidden="true"><Icon /></span>
                            {label} {TEXT.fillFrom}
                        </button>
                        <button
                            type="button"
                            className="button button--light pdf-form-editor__quick-button"
                            onClick={() => handleQuickDateTimeFill(field, 'to')}
                            disabled={isReadOnly}
                        >
                            <span className="button__icon" aria-hidden="true"><Icon /></span>
                            {label} {TEXT.fillTo}
                        </button>
                    </div>
                );
            }
            return (
                <div className="pdf-form-editor__quick-fill">
                    <button
                        type="button"
                        className="button button--light pdf-form-editor__quick-button"
                        onClick={() => handleQuickDateTimeFill(field)}
                        disabled={isReadOnly}
                    >
                        <span className="button__icon" aria-hidden="true"><Icon /></span>
                        {label}
                    </button>
                </div>
            );
        },
        [handleQuickDateTimeFill, isPreviewAutoFill, isReadOnly],
    );


    useEffect(() => {
        if (selectedPrefillId || prefillOptions.length !== 1) return;
        setSelectedPrefillId(String(prefillOptions[0].id));
    }, [prefillOptions, selectedPrefillId]);

    const buildPrefillValues = useCallback(() => {
        if (!template) return {};
        const output = {};
        const templateFields = template.fields || [];
        templateFields
            .filter((field) => field.type !== 'sectionBreak')
            .forEach((field) => {
                if (field.autoFill) return;
                if (field.type === 'text' && field.isReadOnly) return;
                const value = normalizeStoredFieldValue(field, values?.[field.id]);
                if (isFieldValueEmpty(field, value)) return;
                if (field.type === 'checkbox') {
                    output[field.id] = normalizeCheckboxValue(value);
                    return;
                }
                output[field.id] = value;
            });
        return output;
    }, [template, values]);

    const handleApplyPrefill = useCallback(() => {
        if (!template || !selectedPrefillId) return;
        const selected = prefillOptions.find((prefill) => String(prefill.id) === String(selectedPrefillId));
        if (!selected) return;
        const prefillValues = normalizeValuesJson(selected.valuesJson);
        const updates = {};
        (template.fields || [])
            .filter((field) => field.type !== 'sectionBreak')
            .forEach((field) => {
                if (field.autoFill) return;
                if (field.type === 'text' && field.isReadOnly) return;
                if (!Object.prototype.hasOwnProperty.call(prefillValues, field.id)) return;
                const rawValue = normalizeStoredFieldValue(field, prefillValues[field.id]);
                updates[field.id] = field.type === 'checkbox' ? normalizeCheckboxValue(rawValue) : rawValue;
            });
        if (Object.keys(updates).length === 0) return;
        setValues((prev) => {
            const safePrev = prev && typeof prev === 'object' ? prev : {};
            const next = { ...safePrev, ...updates };
            setValuesHistory((history) => {
                const past = [...history.past, safePrev].slice(-HISTORY_LIMIT);
                return { past, future: [] };
            });
            return next;
        });
    }, [prefillOptions, selectedPrefillId, template]);

    const handleOpenPrefillModal = useCallback(() => {
        setPrefillName('');
        setPrefillError('');
        setPrefillFeedback('');
        setIsPrefillModalOpen(true);
    }, []);

    const handleSavePrefill = useCallback(async () => {
        if (!template) return;
        const trimmedName = prefillName.trim();
        if (!trimmedName) {
            setPrefillError(TEXT.prefillSaveError);
            return;
        }
        try {
            setIsSavingPrefill(true);
            setPrefillError('');
            const payload = buildPrefillValues();
            const created = await createTemplatePrefill(template.id, {
                name: trimmedName,
                valuesJson: payload,
            });
            setPrefillOptions((prev) => [...prev, { ...created, valuesJson: payload }]);
            setPrefillFeedback(TEXT.prefillSaveSuccess);
            setIsPrefillModalOpen(false);
        } catch (err) {
            setPrefillError(err.response?.data?.message || TEXT.prefillSaveError);
        } finally {
            setIsSavingPrefill(false);
        }
    }, [buildPrefillValues, prefillName, template]);

    const handleClearAll = useCallback(() => {
        if (isReadOnly) return;
        if (!window.confirm(TEXT.clearConfirm)) return;
        const cleared = {};
        const context = {
            job: formInstance?.job || null,
            user: currentUser || formInstance?.currentUser || formInstance?.user || null,
            formStartDate: formInstance?.createdAt ? new Date(formInstance.createdAt) : null,
        };
        (template?.fields || [])
            .filter((field) => field.type !== 'sectionBreak')
            .forEach((field) => {
                if (NON_INTERACTIVE_TYPES.has(field.type)) return;
                cleared[field.id] = getEmptyFieldValue(field);
            });
        (template?.fields || [])
            .filter((field) => field.type !== 'sectionBreak')
            .forEach((field) => {
                if (!field.autoFill) return;
                if (field.autoFill === 'time_on_click' || field.autoFill === 'date_on_click') return;
                const value = resolveAutoFillValue(field.autoFill, context);
                if (value === '') return;
                cleared[field.id] = isRangeField(field) ? { from: value, to: '' } : value;
            });
        (template?.fields || [])
            .filter((field) => field.type === 'text' && field.isReadOnly && field.staticValue)
            .forEach((field) => {
                cleared[field.id] = field.staticValue;
            });
        (template?.fields || [])
            .filter((field) => field.type === 'date')
            .forEach((field) => {
                const currentValue = cleared[field.id];
                if (isFieldValueEmpty(field, currentValue)) return;
                cleared[field.id] = normalizeDateFieldValue(field, currentValue);
            });
        setValues((prev) => {
            const safePrev = prev && typeof prev === 'object' ? prev : {};
            setValuesHistory((history) => {
                const past = [...history.past, safePrev].slice(-HISTORY_LIMIT);
                return { past, future: [] };
            });
            return cleared;
        });
    }, [currentUser, formInstance, isReadOnly, template]);

    const handleUndo = useCallback(() => {
        setValuesHistory((prev) => {
            if (prev.past.length === 0) return prev;
            const nextPast = [...prev.past];
            const previousValues = nextPast.pop();
            setValues(previousValues || {});
            return {
                past: nextPast,
                future: [values, ...prev.future].slice(0, HISTORY_LIMIT),
            };
        });
    }, [values]);

    const handleRedo = useCallback(() => {
        setValuesHistory((prev) => {
            if (prev.future.length === 0) return prev;
            const [nextValues, ...rest] = prev.future;
            setValues(nextValues || {});
            return {
                past: [...prev.past, values].slice(-HISTORY_LIMIT),
                future: rest,
            };
        });
    }, [values]);

    const persistRecentSuggestionValues = useCallback(
        (submittedValues) => {
            const templateId = template?.id;
            if (!templateId) return;
            const userId = currentUser?.id || formInstance?.userId || '';
            displayFields.forEach((field) => {
                if (!isRecentSuggestionField(field)) return;
                const value = normalizeStoredFieldValue(field, submittedValues?.[field.id]);
                if (isFieldValueEmpty(field, value)) return;
                writeRecentFieldSuggestion({ userId, templateId, field, value });
            });
        },
        [currentUser?.id, displayFields, formInstance?.userId, template?.id],
    );

    const clampPan = useCallback(
        (nextPan, nextZoom) => {
            if (!viewportRef.current) return nextPan;
            const { width, height } = viewportRef.current.getBoundingClientRect();
            const contentWidth = pageSize.width * nextZoom;
            const contentHeight = pageSize.height * nextZoom;
            const offsetX = width - contentWidth;
            const offsetY = height - contentHeight;
            const minX = offsetX < 0 ? offsetX : offsetX / 2;
            const maxX = offsetX < 0 ? 0 : offsetX / 2;
            const minY = offsetY < 0 ? offsetY : offsetY / 2;
            const maxY = offsetY < 0 ? 0 : offsetY / 2;
            return {
                x: clamp(nextPan.x, minX, maxX),
                y: clamp(nextPan.y, minY, maxY),
            };
        },
        [pageSize],
    );

    const applyZoom = useCallback(
        (nextZoom, center) => {
            if (!viewportRef.current) return;
            const clampedZoom = clamp(nextZoom, 0.5, 3);
            const origin = {
                x: (center.x - pan.x) / zoom,
                y: (center.y - pan.y) / zoom,
            };
            const nextPan = {
                x: center.x - origin.x * clampedZoom,
                y: center.y - origin.y * clampedZoom,
            };
            setZoom(clampedZoom);
            setPan(clampPan(nextPan, clampedZoom));
        },
        [clampPan, pan.x, pan.y, zoom],
    );

    const getViewportCenter = useCallback(() => {
        const rect = viewportRef.current?.getBoundingClientRect();
        if (!rect) return { x: 0, y: 0 };
        return { x: rect.width / 2, y: rect.height / 2 };
    }, []);

    const handleZoomIn = () => {
        applyZoom(zoom * 1.1, getViewportCenter());
    };

    const handleZoomOut = () => {
        applyZoom(zoom / 1.1, getViewportCenter());
    };

    const focusField = useCallback(
        (field) => {
            if (!viewportRef.current || !field) return;
            const targetZoom = isCompactLayout ? zoom : 0.9;
            const rect = toRenderRect(field, pageSize);
            if (!rect) return;
            const center = {
                x: rect.x + rect.width / 2,
                y: rect.y + rect.height / 2,
            };
            const { width, height } = viewportRef.current.getBoundingClientRect();
            const nextPan = {
                x: width / 2 - center.x * targetZoom,
                y: height / 2 - center.y * targetZoom,
            };
            setZoom(targetZoom);
            setPan(clampPan(nextPan, targetZoom));
        },
        [clampPan, isCompactLayout, pageSize, zoom],
    );

    const pendingFocusRef = useRef(null);

    useEffect(() => {
        const pending = pendingFocusRef.current;
        if (!pending || pending.pageIndex !== currentPage) return;
        pendingFocusRef.current = null;
        focusField(pending);
    }, [currentPage, pageSize, focusField]);

    const handleFieldSelect = useCallback((field) => {
        if (!field) return;
        if ((field.autoFill === 'time_on_click' || field.autoFill === 'date_on_click') && !isReadOnly) {
            const currentValue = resolveFieldValue(field, values);
            if (isFieldValueEmpty(field, currentValue)) {
                const now = new Date();
                const filledValue = field.autoFill === 'date_on_click'
                    ? formatDateValue(now)
                    : formatTimeValue(now);
                const nextValue = isRangeField(field)
                    ? { ...normalizeRangeValue(currentValue), from: filledValue }
                    : filledValue;
                handleValueChange(field.id, nextValue);
            }
        }
        setActiveFieldId(field.id);
        setActiveSectionIndex(
            Math.max(0, sections.findIndex((section) => section.id === field.sectionId)),
        );
        if (field.pageIndex !== currentPage) {
            pendingFocusRef.current = field;
            setCurrentPage(field.pageIndex);
        } else {
            focusField(field);
        }
        setIsEditorOpen(true);
    }, [currentPage, focusField, handleValueChange, isReadOnly, sections, values]);

    const shouldPreserveEditorFocus = useCallback(
        (targetField) => {
            if (isReadOnly || !isKeyboardNavigationField(activeField) || !isKeyboardNavigationField(targetField)) {
                return false;
            }
            const activeElement = typeof document !== 'undefined' ? document.activeElement : null;
            return Boolean(activeElement?.matches?.('[data-pdf-editor-input="true"]'));
        },
        [activeField, isReadOnly],
    );

    const handleWizardNavPointerDown = useCallback(
        (event, targetField) => {
            if (!shouldPreserveEditorFocus(targetField)) return;
            preserveEditorFocusRef.current = true;
            event.preventDefault();
        },
        [shouldPreserveEditorFocus],
    );

    const handleWizardNavClick = useCallback(
        (targetField) => {
            if (!targetField) return;
            if (shouldPreserveEditorFocus(targetField)) {
                preserveEditorFocusRef.current = true;
            }
            handleFieldSelect(targetField);
        },
        [handleFieldSelect, shouldPreserveEditorFocus],
    );

    const handleEditorTouchStart = useCallback((event) => {
        editorTouchHandledRef.current = false;
        editorTouchStartYRef.current = event.touches?.[0]?.clientY ?? null;
    }, []);

    const handleEditorTouchEnd = useCallback((event) => {
        const startY = editorTouchStartYRef.current;
        editorTouchStartYRef.current = null;
        if (startY === null) return;
        const endY = event.changedTouches?.[0]?.clientY;
        if (!Number.isFinite(endY)) return;
        const deltaY = endY - startY;
        if (deltaY > 36) {
            editorTouchHandledRef.current = true;
            setIsEditorOpen(false);
        } else if (deltaY < -36) {
            editorTouchHandledRef.current = true;
            setIsEditorOpen(true);
        }
    }, []);

    const handleEditorToggleClick = useCallback(() => {
        if (editorTouchHandledRef.current) {
            editorTouchHandledRef.current = false;
            return;
        }
        setIsEditorOpen((prev) => !prev);
    }, []);

    useEffect(() => {
        if (!preserveEditorFocusRef.current) return undefined;
        if (!isKeyboardNavigationField(activeField) || isReadOnly) {
            preserveEditorFocusRef.current = false;
            return undefined;
        }
        const frame = window.requestAnimationFrame(() => {
            preserveEditorFocusRef.current = false;
            const input = editorInputRef.current;
            if (!input || input.disabled) return;
            input.focus({ preventScroll: true });
            if (typeof input.setSelectionRange === 'function') {
                const end = String(input.value || '').length;
                try {
                    input.setSelectionRange(end, end);
                } catch {
                    // Some input types do not expose text selection.
                }
            }
        });
        return () => window.cancelAnimationFrame(frame);
    }, [activeField, activeFieldId, isReadOnly]);

    const handleSectionSelect = useCallback(
        (sectionIndex) => {
            const safeIndex = Math.max(0, Math.min(sectionIndex, sections.length - 1));
            const section = sections[safeIndex];
            const targetField = section?.fields?.[0] || null;
            if (targetField) {
                handleFieldSelect(targetField);
            } else {
                setActiveSectionIndex(safeIndex);
                setActiveFieldId(null);
            }
        },
        [handleFieldSelect, sections],
    );

    useEffect(() => {
        const currentSection = sections[activeSectionIndex];
        const firstField = currentSection?.fields?.[0];
        if (!firstField) return;
        const isActiveInSection = currentSection.fields.some((field) => field.id === activeFieldId);
        const activeSelected = interactiveFields.find((field) => field.id === activeFieldId);
        const fallbackSectionId = sections[0]?.id;
        const activeSectionId = activeSelected?.sectionId || fallbackSectionId;
        const isActiveSkippedInSection =
            Boolean(activeSelected?.skipSequence) && activeSectionId === currentSection?.id;
        if (!activeFieldId || (!isActiveInSection && !isActiveSkippedInSection)) {
            handleFieldSelect(firstField);
        }
    }, [activeSectionIndex, activeFieldId, handleFieldSelect, interactiveFields, sections]);

    const handlePointerDown = (event) => {
        if (event.button && event.button !== 0) return;
        if (event.target.closest('[data-hit="true"]')) return;
        const viewport = viewportRef.current;
        if (!viewport) return;
        viewport.setPointerCapture(event.pointerId);
        const pointers = pointerState.current.pointers;
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (pointers.size === 1) {
            panState.current.isPanning = true;
            panState.current.startX = event.clientX;
            panState.current.startY = event.clientY;
            panState.current.origin = { ...pan };
        }
        if (pointers.size === 2) {
            const [first, second] = Array.from(pointers.values());
            pointerState.current.lastCenter = {
                x: (first.x + second.x) / 2,
                y: (first.y + second.y) / 2,
            };
            pointerState.current.lastDistance = Math.hypot(first.x - second.x, first.y - second.y);
        }
    };

    const handlePointerMove = (event) => {
        const viewport = viewportRef.current;
        if (!viewport) return;
        const pointers = pointerState.current.pointers;
        if (!pointers.has(event.pointerId)) return;
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (pointers.size === 1 && panState.current.isPanning) {
            const dx = event.clientX - panState.current.startX;
            const dy = event.clientY - panState.current.startY;
            const nextPan = {
                x: panState.current.origin.x + dx,
                y: panState.current.origin.y + dy,
            };
            setPan(clampPan(nextPan, zoom));
        }
        if (pointers.size === 2) {
            const [first, second] = Array.from(pointers.values());
            const newCenter = {
                x: (first.x + second.x) / 2,
                y: (first.y + second.y) / 2,
            };
            const newDistance = Math.hypot(first.x - second.x, first.y - second.y);
            if (!pointerState.current.lastCenter) return;
            const scaleChange = newDistance / pointerState.current.lastDistance;
            const rect = viewport.getBoundingClientRect();
            applyZoom(zoom * scaleChange, {
                x: newCenter.x - rect.left,
                y: newCenter.y - rect.top,
            });
            pointerState.current.lastCenter = newCenter;
            pointerState.current.lastDistance = newDistance;
        }
    };

    const handlePointerUp = (event) => {
        const pointers = pointerState.current.pointers;
        pointers.delete(event.pointerId);
        if (pointers.size < 2) {
            pointerState.current.lastCenter = null;
            pointerState.current.lastDistance = 0;
        }
        if (pointers.size === 0) {
            panState.current.isPanning = false;
        }
    };

    const handleSave = async () => {
        if (isPreview) return;
        try {
            setIsSaving(true);
            const payload = values && typeof values === 'object' ? values : {};
            const updated = await updatePdfForm(id, payload);
            setFormInstance(updated);
        } catch (err) {
            if (err.response?.status === 423) {
                setError(TEXT.lockedTitle);
                await refreshLockState();
                return;
            }
            setError(err.response?.data?.message || 'לא ניתן לשמור.');
        } finally {
            setIsSaving(false);
        }
    };

	    const handleMarkDone = async () => {
	        if (isPreview) return;
	        if (!template || !displayFields.length) return;
	        const missingRequired = interactiveFields.filter(
	            (field) => field.required && isFieldValueEmpty(field, resolveFieldValue(field, values)),
	        );
        if (missingRequired.length > 0) {
            setMissingRequiredGroups(buildMissingRequiredGroups(missingRequired));
            setError(`${TEXT.missingRequired} (${missingRequired.length})`);
            handleFieldSelect(missingRequired[0]);
            return;
        }
        setMissingRequiredGroups([]);
	        try {
	            setIsSubmitting(true);
	            const payload = values && typeof values === 'object' ? values : {};
	            await updatePdfForm(id, payload);
	            const filename = buildFormFilename(getExportFormName(template, formInstance), formInstance?.job?.jobNumber);
	            const pdfBlob = await exportPdfFormWithOverlays({
	                formId: id,
	                template,
	                fields: displayFields,
	                values: payload,
	            });
	            await storeCachedPdfFormExport(id, {
	                pdfBlob,
	                filename,
	            });
	            const updated = await submitPdfForm(id);
	            persistRecentSuggestionValues(payload);
	            mergeFormInstance(updated);
            const targetJobId = updated?.jobId || updated?.job?.id || formInstance?.job?.id;
            if (targetJobId) {
                navigate(`/jobs/${targetJobId}`, { replace: true });
            } else {
                navigate('/forms', { replace: true });
            }
        } catch (err) {
            setMissingRequiredGroups([]);
            if (err.response?.status === 423) {
                setError(TEXT.lockedTitle);
                await refreshLockState();
                return;
            }
            setError(err.response?.data?.message || 'לא ניתן לסמן כבוצע.');
        } finally {
            setIsSubmitting(false);
        }
    };

	    const handleExport = async () => {
	        if (isPreview) return;
	        if (!template || !displayFields.length) return;
	        try {
	            setIsExporting(true);
	            const filename = buildFormFilename(getExportFormName(template, formInstance), formInstance?.job?.jobNumber);
	            if (formInstance?.status === 'submitted' && formInstance?.hasCachedPdf) {
	                try {
	                    const cached = await downloadCachedPdfFormExport(id);
	                    saveBlob(cached.blob, filename);
	                    return;
	                } catch (err) {
	                    if (err.response?.status !== 404) {
	                        throw err;
	                    }
	                }
	            }
	            const blob = await exportPdfFormWithOverlays({
	                formId: id,
	                template,
	                fields: displayFields,
	                values,
	            });
	            if (formInstance?.status === 'submitted') {
	                await storeCachedPdfFormExport(id, {
	                    pdfBlob: blob,
	                    filename,
	                });
	            }
	            saveBlob(blob, filename);
	        } catch (err) {
	            setError(err.response?.data?.message || 'לא ניתן לייצא כרגע.');
        } finally {
            setIsExporting(false);
        }
    };

    const handleReopen = async () => {
        if (isPreview) return;
        if (!formInstance) return;
        if (!window.confirm(TEXT.reopenConfirm)) return;
        try {
            setIsReopening(true);
            const updated = await reopenPdfForm(id);
            mergeFormInstance(updated);
        } catch (err) {
            setError(err.response?.data?.message || TEXT.reopenError);
        } finally {
            setIsReopening(false);
        }
    };

    const handleFitWidth = () => {
        setZoom(1);
        setPan(clampPan({ x: 0, y: 0 }, 1));
    };

    const handleFullSize = () => {
        const targetZoom = 1 / fitScale;
        setZoom(targetZoom);
        setPan(clampPan({ x: 0, y: 0 }, targetZoom));
    };

    const renderPrimaryToolbarAction = () => {
        if (isPreview) {
            return (
                <button
                    type="button"
                    className="button button--primary"
                    onClick={handleOpenPrefillModal}
                    disabled={isSavingPrefill}
                >
                    {isSavingPrefill ? TEXT.saving : TEXT.prefillSave}
                </button>
            );
        }
        if (canReopenForm) {
            return (
                <button
                    type="button"
                    className="button button--light"
                    onClick={handleReopen}
                    disabled={isReopening}
                >
                    {isReopening ? TEXT.reopening : TEXT.reopen}
                </button>
            );
        }
        return (
            <button
                type="button"
                className="button button--primary"
                onClick={handleMarkDone}
                disabled={isSubmitting || isReadOnly}
            >
                {isSubmitting ? TEXT.markingDone : TEXT.markDone}
            </button>
        );
    };

    const renderClearToolbarAction = () => {
        if (isPreview) {
            return null;
        }
        return (
            <button
                type="button"
                className="button button--danger"
                onClick={handleClearAll}
                disabled={isReadOnly}
            >
                {TEXT.clearAll}
            </button>
        );
    };

    const renderActiveSuggestionList = () => {
        if (activeSuggestionGroups.length === 0) return null;
        return (
            <div className="pdf-form-editor__suggestions" role="listbox">
                {activeSuggestionGroups.map((group) => (
                    <div key={group.id} className="pdf-form-editor__suggestion-group">
                        <div className="pdf-form-editor__suggestion-heading">{group.label}</div>
                        {group.options.map((option) => (
                            <button
                                key={`${group.id}-${option}`}
                                type="button"
                                className="pdf-form-editor__suggestion"
                                onClick={() => handleSuggestionPick(option)}
                            >
                                {option}
                            </button>
                        ))}
                    </div>
                ))}
            </div>
        );
    };

    if (!formInstance || !template) {
        return (
            <div className="page stack">
                <button type="button" className="button button--ghost" onClick={() => navigate(-1)}>
                    {TEXT.back}
                </button>
                <div className="card">{TEXT.loading}</div>
            </div>
        );
    }

    return (
        <div
            className="page pdf-form-fill"
            style={{
                '--pdf-form-toolbar-height': `${toolbarHeight}px`,
                '--pdf-form-toolbar-top': `${siteHeaderHeight + 16}px`,
                '--pdf-form-site-header-height': `${siteHeaderHeight}px`,
            }}
        >
            <header className="pdf-form-fill__header">
                <div className="pdf-form-fill__header-left">
                    <div className="pdf-form-fill__title">
                        <button type="button" className="button button--ghost" onClick={() => navigate(-1)}>
                            {TEXT.back}
                        </button>
                        <h1 className="page__title">{template.name}</h1>
                    </div>
                    {!isPreview && prefillOptions.length > 0 && (
                        <div className="pdf-form-fill__prefill">
                            <label className="form-field">
                                <span className="form-field__label">{TEXT.prefillLabel}</span>
                                <select
                                    className="form-field__control"
                                    value={selectedPrefillId}
                                    onChange={(event) => setSelectedPrefillId(event.target.value)}
                                    disabled={isReadOnly}
                                >
                                    <option value="">{TEXT.prefillSelect}</option>
                                    {prefillOptions.map((prefill) => (
                                        <option key={prefill.id} value={prefill.id}>
                                            {prefill.name}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <button
                                type="button"
                                className="button button--light"
                                onClick={handleApplyPrefill}
                                disabled={!selectedPrefillId || isReadOnly}
                            >
                                {TEXT.prefillApply}
                            </button>
                        </div>
                    )}
                </div>
                <div className="pdf-form-fill__header-right">
                    <div className="pdf-form-fill__status">
                        {currentUser && (
                            <div className="pdf-form-fill__user">
                                {currentUserAvatarUrl ? (
                                    <img
                                        src={currentUserAvatarUrl}
                                        alt={currentUserLabel || 'User'}
                                        className="pdf-form-fill__avatar"
                                    />
                                ) : (
                                    <span className="pdf-form-fill__avatar pdf-form-fill__avatar--fallback">
                                        {currentUserInitials}
                                    </span>
                                )}
                            </div>
                        )}
                        {!isPreview && (
                            <div className="pdf-form-fill__save-state">
                                {isSaving ? TEXT.saving : lastSavedLabel ? `${TEXT.lastSaved}: ${lastSavedLabel}` : ''}
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <div className="pdf-form-fill__alerts">
                {isLocked && (
                    <div className="alert alert--warning">
                        <strong>{TEXT.lockedTitle}</strong>{' '}
                        {TEXT.lockedHint}{' '}
                        {lockedBy?.name || lockedBy?.email || TEXT.lockedUnknown}. {TEXT.lockedViewOnly}
                    </div>
                )}
                {isPreview && prefillFeedback && (
                    <div className="alert alert--success">{prefillFeedback}</div>
                )}
                {error && (
                    <div className="alert alert--danger">
                        <div>{error}</div>
                        {showMissingRequiredSummary && (
                            <div className="pdf-form-fill__missing-summary">
                                {missingRequiredGroups.map((group, index) => (
                                    <details
                                        key={`missing-page-${group.pageIndex}`}
                                        className="pdf-form-fill__missing-group"
                                        open={index === 0}
                                    >
                                        <summary className="pdf-form-fill__missing-group-summary">
                                            {`${TEXT.page} ${group.pageIndex + 1} • ${group.count} שדות חסרים`}
                                        </summary>
                                        <div className="pdf-form-fill__missing-fields">
                                            {group.fields.map((field) => (
                                                <button
                                                    key={field.id}
                                                    type="button"
                                                    className="pdf-form-fill__missing-field"
                                                    title={field.fullLabel || field.label}
                                                    onClick={() => {
                                                        const targetField = interactiveFields.find((item) => item.id === field.id);
                                                        if (targetField) {
                                                            handleFieldSelect(targetField);
                                                        }
                                                    }}
                                                >
                                                    {field.label}
                                                </button>
                                            ))}
                                        </div>
                                    </details>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
            <div
                className={`pdf-form-fill__toolbar${isMobileToolbarExpanded ? ' is-expanded' : ' is-collapsed'}`}
                ref={toolbarRef}
            >
                <button
                    type="button"
                    className="icon-button icon-button--compact pdf-form-fill__toolbar-toggle"
                    onClick={() => setIsMobileToolbarExpanded((prev) => !prev)}
                    aria-expanded={isMobileToolbarExpanded}
                    aria-label={isMobileToolbarExpanded ? TEXT.toolbarHide : TEXT.toolbarShow}
                    title={isMobileToolbarExpanded ? TEXT.toolbarHide : TEXT.toolbarShow}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path
                            d={isMobileToolbarExpanded ? 'M6 15l6-6 6 6' : 'M6 9l6 6 6-6'}
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
                <div className="pdf-form-fill__toolbar-group pdf-form-fill__toolbar-group--actions">
                    {renderPrimaryToolbarAction()}
                    {renderClearToolbarAction()}
                </div>
                <div className="pdf-form-fill__toolbar-group pdf-form-fill__toolbar-group--zoom">
                    <button
                        type="button"
                        className="icon-button icon-button--compact"
                        onClick={handleZoomOut}
                        title={TEXT.zoomOut}
                    >
                        <span className="button__icon" aria-hidden="true">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M6 12h12"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </span>
                    </button>
                    <span className="pdf-form-fill__viewer-label pdf-form-fill__zoom-label">
                        {TEXT.zoom} {Math.round(zoom * 100)}%
                    </span>
                    <button
                        type="button"
                        className="icon-button icon-button--compact"
                        onClick={handleZoomIn}
                        title={TEXT.zoomIn}
                    >
                        <span className="button__icon" aria-hidden="true">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M6 12h12M12 6v12"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </span>
                    </button>
                    <button type="button" className="button button--ghost pdf-form-fill__fit-button" onClick={handleFitWidth}>
                        {TEXT.fit}
                    </button>
                    <button type="button" className="button button--ghost pdf-form-fill__fit-button" onClick={handleFullSize}>
                        {TEXT.fullSize}
                    </button>
                </div>
                <div className="pdf-form-fill__toolbar-group pdf-form-fill__toolbar-group--pages">
                    <button
                        type="button"
                        className="icon-button icon-button--compact"
                        onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                        disabled={currentPage === 0}
                        title={`${TEXT.page} הקודם`}
                    >
                        <span className="button__icon" aria-hidden="true">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M9 6l6 6-6 6"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                    </button>
                    <span className="pdf-form-fill__viewer-label pdf-form-fill__page-label">
                        {TEXT.page} {currentPage + 1} {TEXT.pageOf} {pageCount}
                    </span>
                    <button
                        type="button"
                        className="icon-button icon-button--compact"
                        onClick={() => setCurrentPage((prev) => Math.min(pageCount - 1, prev + 1))}
                        disabled={currentPage >= pageCount - 1}
                        title={`${TEXT.page} הבא`}
                    >
                        <span className="button__icon" aria-hidden="true">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M15 18l-6-6 6-6"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                    </button>
                </div>
                <div className="pdf-form-fill__toolbar-group pdf-form-fill__toolbar-group--history">
                    <button
                        type="button"
                        className="button button--light pdf-form-fill__toolbar-icon-button"
                        onClick={handleRedo}
                        disabled={!canRedo}
                        aria-label={TEXT.redo}
                        title={TEXT.redo}
                    >
                        <span className="button__icon" aria-hidden="true">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M15 5l5 5-5 5"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M4 19v-4a5 5 0 0 1 5-5h10"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                        <span className="pdf-form-fill__button-text">{TEXT.redo}</span>
                    </button>
                    <button
                        type="button"
                        className="button button--light pdf-form-fill__toolbar-icon-button"
                        onClick={handleUndo}
                        disabled={!canUndo}
                        aria-label={TEXT.undo}
                        title={TEXT.undo}
                    >
                        <span className="button__icon" aria-hidden="true">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M9 5l-5 5 5 5"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M20 19v-4a5 5 0 0 0-5-5H5"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                        <span className="pdf-form-fill__button-text">{TEXT.undo}</span>
                    </button>
                </div>
            </div>

            <div className="pdf-form-fill__body">
                <div className="pdf-form-fill__viewer">
                    <div className="pdf-viewer">
                        <div
                            className="pdf-viewer__viewport"
                            ref={viewportRef}
                            onPointerDown={handlePointerDown}
                            onPointerMove={handlePointerMove}
                            onPointerUp={handlePointerUp}
                            onPointerCancel={handlePointerUp}
                        >
                            <div
                                className="pdf-viewer__stage"
                                style={{
                                    width: pageSize.width,
                                    height: pageSize.height,
                                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                                }}
                            >
                                <canvas ref={canvasRef} className="pdf-viewer__canvas" />
                                <div className="pdf-viewer__overlay">
                                    {displayFields
                                        .filter((field) => field.pageIndex === currentPage)
                                        .map((field) => {
                                            const renderRect = toRenderRect(field, pageSize);
                                            if (!renderRect) return null;
                                            const isInteractive = interactiveFieldIds.has(field.id);
                                            const value = resolveFieldValue(field, values);
                                            const displayValue = getValueDisplay(field, value);
                                            const direction = resolveDirection(field, displayValue);
                                            const align = resolveAlign(field, direction);
                                            const justify = resolveJustifyContent(align, direction);
                                            const rectStyle = field.type === 'rectangle'
                                                ? normalizeRectStyle(field.styleJson)
                                                : null;
                                            const imageSrc = field.type === 'image' ? field.assetDataUrl : '';
                                            const hitRect = isInteractive ? toHitRect(renderRect, pageSize) : null;
                                            const baseStyle = {
                                                left: renderRect.x,
                                                top: renderRect.y,
                                                width: renderRect.width,
                                                height: renderRect.height,
                                            };
                                            if (field.type === 'rectangle') {
                                                const strokeWidth = Math.max(0, Number(rectStyle?.strokeWidth) || 0);
                                                const strokeColor = rectStyle?.strokeColor || '#2563eb';
                                                const fillColor = rectStyle?.fillEnabled
                                                    ? rectStyle?.fillColor || 'transparent'
                                                    : 'transparent';
                                                const opacity = Number.isFinite(rectStyle?.opacity) ? rectStyle.opacity : 1;
                                                return (
                                                    <div
                                                        key={field.id}
                                                        className="pdf-field-render pdf-field-render--shape"
                                                        style={{
                                                            ...baseStyle,
                                                            backgroundColor: fillColor,
                                                            border: strokeWidth > 0 ? `${strokeWidth}px solid ${strokeColor}` : 'none',
                                                            opacity,
                                                        }}
                                                    />
                                                );
                                            }
                                            return (
                                                <React.Fragment key={field.id}>
                                                    {isInteractive && hitRect && (
                                                        <button
                                                            type="button"
                                                            data-hit="true"
                                                            className="pdf-field-hit"
                                                            style={{
                                                                left: hitRect.x,
                                                                top: hitRect.y,
                                                                width: hitRect.width,
                                                                height: hitRect.height,
                                                            }}
                                                            onClick={() => handleFieldSelect(field)}
                                                        />
                                                    )}
                                                    {field.type === 'image' ? (
                                                        imageSrc ? (
                                                            <div
                                                                className="pdf-field-render pdf-field-render--image"
                                                                style={baseStyle}
                                                            >
                                                                <img src={imageSrc} alt={field.label || 'image'} />
                                                            </div>
                                                        ) : null
                                                    ) : (
                                                        field.type === 'signature' && value ? (
                                                            <div
                                                                className={`pdf-field-render pdf-field-render--signature${activeFieldId === field.id ? ' active' : ''}`}
                                                                style={{
                                                                    ...baseStyle,
                                                                    textAlign: align,
                                                                    direction,
                                                                    justifyContent: justify,
                                                                }}
                                                            >
                                                                <img src={value} alt="signature" />
                                                            </div>
                                                        ) : (
                                                            <PdfFieldTextBox
                                                                field={field}
                                                                values={values}
                                                                className="pdf-field-render"
                                                                fontScale={fitScale}
                                                                isActive={activeFieldId === field.id}
                                                                style={baseStyle}
                                                            />
                                                        )
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <aside className="pdf-form-fill__panel pdf-form-fill__wizard">
                    <div className="pdf-form-fill__wizard-scroll">
                    <div
                        className={`pdf-form-editor${isEditorOpen ? ' open' : ''}`}
                    >
                        <button
                            type="button"
                            className="pdf-form-editor__mobile-toggle"
                            onClick={handleEditorToggleClick}
                            onTouchStart={handleEditorTouchStart}
                            onTouchEnd={handleEditorTouchEnd}
                            aria-expanded={isEditorOpen}
                        >
                            <span className="pdf-form-editor__mobile-grip" aria-hidden="true" />
                            <span>{isEditorOpen ? TEXT.editorHide : TEXT.editorShow}</span>
                        </button>
                        {activeField ? (
                            <>
                                <header className="pdf-form-editor__header">
                                    <div className="stack">
                                        <h3>{activeField.label || activeField.type}</h3>
                                        {activeField.subtext && (
                                            <p className="pdf-form-editor__subtext">{activeField.subtext}</p>
                                        )}
                                    </div>
                                </header>
                                <div className="pdf-form-editor__body">
                                    {isPreviewAutoFill && (
                                        <div className="pdf-form-editor__readonly">
                                            {TEXT.autoFillLabel}
                                        </div>
                                    )}
                                    {!isPreviewAutoFill && activeField.type === 'text' && activeField.isReadOnly && (
                                        <div className="pdf-form-editor__readonly">
                                            {activeFieldDisplayValue || activeField.placeholder || ''}
                                        </div>
                                    )}
                                    {!isPreviewAutoFill && activeField.type === 'text' && !activeField.isReadOnly && activeField.isMultiline && (
                                        <div className="pdf-form-editor__suggestion-wrap">
                                            <textarea
                                                ref={editorInputRef}
                                                data-pdf-editor-input="true"
                                                className="form-field__control form-field__control--textarea"
                                                rows={4}
                                                value={activeFieldValue || ''}
                                                onChange={(event) => handleValueChange(activeField.id, event.target.value)}
                                                onKeyDown={handleSuggestionKeyDown}
                                                placeholder={activeField.placeholder || ''}
                                                dir={activeDirection}
                                                style={{ textAlign: activeAlign }}
                                                disabled={isReadOnly}
                                            />
                                            {renderActiveSuggestionList()}
                                        </div>
                                    )}
                                    {!isPreviewAutoFill && activeField.type === 'text' && !activeField.isReadOnly && !activeField.isMultiline && (
                                        <div className="pdf-form-editor__suggestion-wrap">
                                            <input
                                                ref={editorInputRef}
                                                data-pdf-editor-input="true"
                                                className="form-field__control"
                                                type="text"
                                                value={activeFieldValue || ''}
                                                onChange={(event) => handleValueChange(activeField.id, event.target.value)}
                                                onKeyDown={handleSuggestionKeyDown}
                                                placeholder={activeField.placeholder || ''}
                                                dir={activeDirection}
                                                style={{ textAlign: activeAlign }}
                                                disabled={isReadOnly}
                                            />
                                            {renderActiveSuggestionList()}
                                        </div>
                                    )}
                                    {!isPreviewAutoFill && activeField.type === 'number' && (
                                        <div className="pdf-form-editor__suggestion-wrap">
                                            <input
                                                ref={editorInputRef}
                                                data-pdf-editor-input="true"
                                                className="form-field__control"
                                                type="number"
                                                value={values[activeField.id] || ''}
                                                onChange={(event) => handleValueChange(activeField.id, event.target.value)}
                                                onKeyDown={handleSuggestionKeyDown}
                                                placeholder={activeField.placeholder || ''}
                                                min={activeField.numericConfigJson?.min ?? undefined}
                                                max={activeField.numericConfigJson?.max ?? undefined}
                                                step={activeField.numericConfigJson?.decimals ? 0.1 : 1}
                                                dir={activeDirection}
                                                style={{ textAlign: activeAlign }}
                                                disabled={isReadOnly}
                                            />
                                            {renderActiveSuggestionList()}
                                        </div>
                                    )}
                                    {!isPreviewAutoFill && activeField.type === 'dropdown' && (
                                        <select
                                            className="form-field__control"
                                            value={values[activeField.id] || ''}
                                            onChange={(event) => handleValueChange(activeField.id, event.target.value)}
                                            dir={activeDirection}
                                            style={{ textAlign: activeAlign }}
                                            disabled={isReadOnly}
                                        >
                                            <option value="">בחר...</option>
                                            {normalizeOptions(activeField.optionsJson).map((option) => (
                                                <option key={option} value={option}>
                                                    {option}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                    {!isPreviewAutoFill && activeField.type === 'checkbox' && (
                                        <button
                                            type="button"
                                            className="check-toggle__option"
                                            onClick={() =>
                                                handleValueChange(activeField.id, getNextCheckboxValue(values[activeField.id]))
                                            }
                                            disabled={isReadOnly}
                                        >
                                            <span className="check-toggle__symbol">
                                                {getCheckboxDisplayValue(values[activeField.id])}
                                            </span>
                                            <span>{activeField.label || ''}</span>
                                        </button>
                                    )}
                                    {!isPreviewAutoFill && activeField.type === 'date' && (
                                        <>
                                        {isRangeField(activeField) ? (
                                            <div className="pdf-form-editor__range">
                                                <label className="form-field">
                                                    <span className="form-field__label">מ-</span>
                                                    <input
                                                        ref={editorInputRef}
                                                        data-pdf-editor-input="true"
                                                        className="form-field__control"
                                                        type="text"
                                                        inputMode="text"
                                                        placeholder="dd/mm/yyyy"
                                                        value={activeRangeValue.from}
                                                        onChange={(event) =>
                                                            handleValueChange(
                                                                activeField.id,
                                                                setRangeSideValue(activeField, activeFieldValue, 'from', event.target.value),
                                                            )
                                                        }
                                                        dir="ltr"
                                                        disabled={isReadOnly}
                                                    />
                                                </label>
                                                <label className="form-field">
                                                    <span className="form-field__label">עד</span>
                                                    <input
                                                        data-pdf-editor-input="true"
                                                        className="form-field__control"
                                                        type="text"
                                                        inputMode="text"
                                                        placeholder="dd/mm/yyyy"
                                                        value={activeRangeValue.to}
                                                        onChange={(event) =>
                                                            handleValueChange(
                                                                activeField.id,
                                                                setRangeSideValue(activeField, activeFieldValue, 'to', event.target.value),
                                                            )
                                                        }
                                                        dir="ltr"
                                                        disabled={isReadOnly}
                                                    />
                                                </label>
                                            </div>
                                        ) : (
                                            <input
                                                ref={editorInputRef}
                                                data-pdf-editor-input="true"
                                                className="form-field__control"
                                                type="text"
                                                inputMode="text"
                                                placeholder="dd/mm/yyyy"
                                                value={activeFieldValue || ''}
                                                onChange={(event) =>
                                                    handleValueChange(activeField.id, event.target.value)
                                                }
                                                dir={activeDirection}
                                                style={{ textAlign: activeAlign }}
                                                disabled={isReadOnly}
                                            />
                                        )}
                                        {renderDateTimeQuickFill(activeField)}
                                        </>
                                    )}
                                    {!isPreviewAutoFill && activeField.type === 'time' && (
                                        <>
                                        {isRangeField(activeField) ? (
                                            <div className="pdf-form-editor__range">
                                                <label className="form-field">
                                                    <span className="form-field__label">מ-</span>
                                                    <input
                                                        className="form-field__control"
                                                        type="time"
                                                        value={activeRangeValue.from}
                                                        onChange={(event) =>
                                                            handleValueChange(
                                                                activeField.id,
                                                                setRangeSideValue(activeField, activeFieldValue, 'from', event.target.value),
                                                            )
                                                        }
                                                        dir="ltr"
                                                        disabled={isReadOnly}
                                                    />
                                                </label>
                                                <label className="form-field">
                                                    <span className="form-field__label">עד</span>
                                                    <input
                                                        className="form-field__control"
                                                        type="time"
                                                        value={activeRangeValue.to}
                                                        onChange={(event) =>
                                                            handleValueChange(
                                                                activeField.id,
                                                                setRangeSideValue(activeField, activeFieldValue, 'to', event.target.value),
                                                            )
                                                        }
                                                        dir="ltr"
                                                        disabled={isReadOnly}
                                                    />
                                                </label>
                                            </div>
                                        ) : (
                                            <input
                                                className="form-field__control"
                                                type="time"
                                                value={activeFieldValue || ''}
                                                onChange={(event) => handleValueChange(activeField.id, event.target.value)}
                                                dir={activeDirection}
                                                style={{ textAlign: activeAlign }}
                                                disabled={isReadOnly}
                                            />
                                        )}
                                        {renderDateTimeQuickFill(activeField)}
                                        </>
                                    )}
                                    {!isPreviewAutoFill && activeField.type === 'signature' && (
                                        <div className="signature-field">
                                            {values[activeField.id] ? (
                                                <img
                                                    src={values[activeField.id]}
                                                    alt="signature"
                                                    className="signature-preview"
                                                />
                                            ) : (
                                                <div className="card__meta">{TEXT.signatureTitle}</div>
                                            )}
                                            <button
                                                type="button"
                                                className="button button--primary"
                                                onClick={() => setIsSignatureOpen(true)}
                                                disabled={isReadOnly}
                                            >
                                                {values[activeField.id] ? TEXT.updateSignature : TEXT.openSignature}
                                            </button>
                                        </div>
                                    )}
                                    {(previousNavigableField || nextNavigableField || (!isPreview && isLastAccessibleField)) && (
                                        <div className="pdf-form-editor__nav pdf-form-editor__nav--below">
                                            <button
                                                type="button"
                                                className="button button--light"
                                                onPointerDown={(event) =>
                                                    handleWizardNavPointerDown(event, previousNavigableField)
                                                }
                                                onClick={() => handleWizardNavClick(previousNavigableField)}
                                                disabled={!previousNavigableField}
                                            >
                                                {TEXT.previous}
                                            </button>
                                            <button
                                                type="button"
                                                className="button button--light"
                                                onPointerDown={(event) =>
                                                    handleWizardNavPointerDown(event, nextNavigableField)
                                                }
                                                onClick={() => handleWizardNavClick(nextNavigableField)}
                                                disabled={!nextNavigableField}
                                            >
                                                {TEXT.next}
                                            </button>
                                            {!isPreview && isLastAccessibleField && (
                                                <button
                                                    type="button"
                                                    className="button button--primary"
                                                    onClick={handleMarkDone}
                                                    disabled={isSubmitting || isReadOnly}
                                                >
                                                    {isSubmitting ? TEXT.markingDone : TEXT.done}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {(isCompactLayout || (isLastFieldInSection && hasNextSection)) && (
                                    <footer className="pdf-form-editor__footer">
                                        <button
                                            type="button"
                                            className="button button--primary"
                                            onPointerDown={(event) => {
                                                if (isLastFieldInSection && hasNextSection) {
                                                    handleWizardNavPointerDown(event, nextSectionFirstField);
                                                }
                                            }}
                                            onClick={() => {
                                                if (isLastFieldInSection && hasNextSection) {
                                                    if (nextSectionFirstField) {
                                                        handleWizardNavClick(nextSectionFirstField);
                                                        return;
                                                    }
                                                    handleSectionSelect(activeSectionIndex + 1);
                                                    return;
                                                }
                                                setIsEditorOpen(false);
                                            }}
                                        >
                                            {isLastFieldInSection && hasNextSection ? TEXT.nextSection : TEXT.close}
                                        </button>
                                    </footer>
                                )}
                            </>
                        ) : (
                            <div className="card__meta">{TEXT.emptyFields}</div>
                        )}
                    </div>

                    <div className="pdf-form-fill__section-separator" />
                    <section className="pdf-form-fill__section-nav">
                        <h2 className="pdf-form-fill__section-heading">{TEXT.sections}</h2>
                        <div className="pdf-form-panel__sections">
                            <div className="section-list">
                                {sections.map((section, index) => (
                                    <button
                                        key={section.id || index}
                                        type="button"
                                        className={`section-pill${activeSectionIndex === index ? ' active' : ''}`}
                                        onClick={() => handleSectionSelect(index)}
                                    >
                                        {section.title || `שלב ${index + 1}`}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </section>
                    </div>
                </aside>
            </div>

            {isPreview && isPrefillModalOpen && (
                <div className="modal-overlay" role="dialog" aria-modal="true">
                    <div className="modal-content">
                        <header className="stack">
                            <h2 className="card__title">{TEXT.prefillSave}</h2>
                        </header>
                        <div className="modal-form">
                            <div className="modal-form__body">
                                <label className="form-field">
                                    <span className="form-field__label">{TEXT.prefillName}</span>
                                    <input
                                        className="form-field__control"
                                        type="text"
                                        value={prefillName}
                                        onChange={(event) => setPrefillName(event.target.value)}
                                        disabled={isSavingPrefill}
                                        required
                                    />
                                </label>
                                {prefillError && <div className="alert alert--danger">{prefillError}</div>}
                            </div>
                            <footer className="modal-form__footer">
                                <button
                                    type="button"
                                    className="button button--ghost"
                                    onClick={() => setIsPrefillModalOpen(false)}
                                    disabled={isSavingPrefill}
                                >
                                    {TEXT.close}
                                </button>
                                <button
                                    type="button"
                                    className="button button--primary"
                                    onClick={handleSavePrefill}
                                    disabled={isSavingPrefill}
                                >
                                    {isSavingPrefill ? TEXT.saving : TEXT.prefillSave}
                                </button>
                            </footer>
                        </div>
                    </div>
                </div>
            )}

            {isSignatureOpen && (
                <div className="modal-overlay" role="dialog" aria-modal="true">
                    <div className="modal-content signature-modal">
                        <header className="stack">
                            <h2 className="card__title">{TEXT.signatureTitle}</h2>
                        </header>
                        <SignaturePad
                            value={activeField ? values[activeField.id] || '' : ''}
                            onChange={(value) => {
                                if (activeField) {
                                    handleValueChange(activeField.id, value);
                                }
                            }}
                        />
                        <footer className="modal-form__footer">
                            <button type="button" className="button button--ghost" onClick={() => setIsSignatureOpen(false)}>
                                {TEXT.close}
                            </button>
                            <button type="button" className="button button--primary" onClick={() => setIsSignatureOpen(false)}>
                                {TEXT.done}
                            </button>
                        </footer>
                    </div>
                </div>
            )}
        </div>
    );
}
